import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import { ShieldCheck, BadgeCheck, Star, MapPin, Lock, Camera, FileCheck } from "lucide-react";

export default function ClientInviteLanding() {
  const { artisanId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [artisan, setArtisan] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    apiClient.get(`/artisans/${artisanId}`)
      .then(({ data }) => setArtisan(data.artisan || data))
      .catch(() => setNotFound(true));
  }, [artisanId]);

  const target = `/missions/new?invite=${artisanId}`;
  const go = () => {
    if (user?.role === "demandeur") nav(target);
    else if (!user) nav(`/register?role=demandeur&next=${encodeURIComponent(target)}`);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F7F7F9]">
        <Navbar />
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <div className="display text-3xl">Lien d'invitation invalide</div>
          <p className="text-zinc-600 mt-3">Cet artisan n'existe pas ou n'est plus inscrit sur Batisur.</p>
        </div>
      </div>
    );
  }
  if (!artisan) return <div className="min-h-screen bg-[#F7F7F9]"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00]">// Invitation personnelle</div>
        <h1 className="display text-4xl sm:text-5xl mt-3">
          {artisan.name} vous invite à sécuriser votre chantier.
        </h1>
        <p className="mt-4 text-zinc-700 max-w-2xl">
          Votre artisan travaille avec Batisur : votre paiement est <strong>bloqué sur un compte séquestre Stripe</strong> et
          n'est versé qu'après <strong>votre validation des travaux</strong>, preuves photo à l'appui. Zéro mauvaise surprise.
        </p>

        <div className="mt-8 border-2 border-[#0A0A0B] bg-white p-6" data-testid="invite-artisan-card">
          <div className="flex flex-wrap items-center gap-4">
            <div className="w-14 h-14 bg-[#0A0A0B] text-white flex items-center justify-center display text-2xl shrink-0">
              {artisan.name?.[0]?.toUpperCase() || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="display text-2xl">{artisan.name}</div>
              <div className="text-sm text-zinc-600 flex items-center gap-2 flex-wrap">
                {artisan.company && <span>{artisan.company}</span>}
                {artisan.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{artisan.city}</span>}
                {artisan.rating_count > 0 && (
                  <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-[#FF4D00]" />{Number(artisan.rating_avg).toFixed(1)} ({artisan.rating_count})</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {artisan.siret_verified && (
                  <span className="mono text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-400 px-2 py-0.5 flex items-center gap-1">
                    <BadgeCheck className="w-3 h-3" /> SIRET vérifié
                  </span>
                )}
                {artisan.has_decennale && (
                  <span className="mono text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-300 px-2 py-0.5 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Assuré décennale
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-0 border-2 border-[#0A0A0B] bg-white">
          {[
            [FileCheck, "1. Décrivez votre chantier", "2 minutes, photos à l'appui. Votre artisan est automatiquement invité."],
            [Lock, "2. Payez en séquestre", "Le montant du devis est bloqué chez Stripe — pas versé à l'artisan."],
            [Camera, "3. Validez, on paie", "Preuves photo horodatées, vous validez, l'artisan est payé."],
          ].map(([Icon, t, d], i) => (
            <div key={t} className={`p-6 ${i < 2 ? "sm:border-r-2 border-b-2 sm:border-b-0 border-[#0A0A0B]" : ""}`}>
              <Icon className="w-6 h-6 text-[#FF4D00]" />
              <div className="font-bold mt-3">{t}</div>
              <div className="text-sm text-zinc-600 mt-1">{d}</div>
            </div>
          ))}
        </div>

        {user?.role === "artisan" ? (
          <div className="mt-8 border-2 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900" data-testid="invite-artisan-warning">
            Vous êtes connecté en tant qu'artisan — ce lien est destiné à vos clients particuliers.
          </div>
        ) : (
          <button onClick={go} className="btn-batisur w-full mt-8 text-lg" data-testid="invite-start-btn">
            Créer ma demande avec {artisan.name} →
          </button>
        )}
        <p className="text-[11px] text-zinc-500 mt-3 text-center">
          Gratuit à l'inscription · Paiement 100% sécurisé par Stripe · Certificat de bonne fin en fin de chantier
        </p>
      </div>
    </div>
  );
}
